import axios, { AxiosError } from 'axios';
import { stringify } from 'qs';
import { toast } from 'react-toastify';

import { Auth } from '@aws-amplify/auth';

import { useAuthStore } from './store/authStore';

interface ErrorData {
  message: string;
  title: string;
}

const createAxios = (baseURL: any) => {
  const newInstance = axios.create({
    baseURL: baseURL,
    headers: {
      'Content-Type': 'application/json'
    },

    paramsSerializer: (params) => stringify(params)
  });

  newInstance.interceptors.request.use(
    async (options) => {
      try {
        const session = await Auth.currentSession();
        if (options.headers) {
          options.headers['Authorization'] =
            `Bearer ${session.getIdToken().getJwtToken()}`;
        }

        return options;
      } catch {
        return {
          ...options,
          cancelToken: new axios.CancelToken((cancel) => cancel())
        };
      }
    },
    (error) => Promise.reject(error)
  );

  newInstance.interceptors.response.use(
    (res) => res,
    (error: AxiosError<ErrorData>) => {
      if (error.response && error.response.status === 409) {
        toast(error.response?.data.message || 'Conflict Error', {
          type: 'error'
        });
      } else if (error.response && error.response.status === 422) {
        toast(error.response?.data.message || 'Input Error', {
          type: 'error'
        });
      } else if (error.response && error.response.status === 404) {
        toast(error.response?.data.message || 'Affiliate Not Found', {
          type: 'error'
        });
        // Dev-only: skip the auto-sign-out so the dashboard shell stays viewable
        // even when the backend has no matching affiliate record (e.g. wrong environment/test data).
        if (process.env.NODE_ENV !== 'development') {
          const { setUserId } = useAuthStore.getState();
          Auth.signOut().then(() => setUserId());
        }
      } else {
        toast(error.response?.data.message || 'Server Error', {
          type: 'error'
        });
      }
      return Promise.reject(error);
    }
  );

  return newInstance;
};

// Unauthenticated client. `createAxios` attaches a Cognito bearer token and cancels
// any request made without a session, so endpoints that are called before login
// (e.g. phone-number lookup) have to go through an instance with no auth interceptor.
const createUnauthenticatedAxios = (baseURL: any) => {
  const newInstance = axios.create({
    baseURL: baseURL,
    headers: {
      'Content-Type': 'application/json'
    },
    paramsSerializer: (params) => stringify(params)
  });

  newInstance.interceptors.response.use(
    (res) => res,
    (error: AxiosError<ErrorData>) => Promise.reject(error)
  );

  return newInstance;
};

// In development, requests go through the CRA dev server's own proxy (see src/setupProxy.js)
// instead of straight to the external API, so the browser never makes a cross-origin request
// and CORS never applies. Production keeps the direct absolute URL.
const baseURL =
  process.env.NODE_ENV === 'development'
    ? ''
    : process.env.REACT_APP_AFFILIATE_SERVICE_API_URL;

const client = createAxios(baseURL);
const apiClient = createUnauthenticatedAxios(baseURL);

export default client;
export { apiClient };
